import type { Primitive } from './types';

export const META = 'metaType' as const;

export type DefinedMetaOf<T> = T extends { [META]: infer X } ? X : {};

export type KeyOf<T> = {
  [K in keyof T]: T[K] extends Function ? never : K
}[Exclude<keyof T, number | 'prototype' | typeof META>];

export type StrictValueOf<T> = T[KeyOf<T>];

export type ValueOf<T> = Primitive<StrictValueOf<T>>;

export type MetaOf<T> = {
  value: StrictValueOf<T>;
  key: KeyOf<T>;
} & DefinedMetaOf<T>;

type MetaMap<T> = Map<KeyOf<T>, Readonly<MetaOf<T>>>;

const MetaMapCollection = new WeakMap<EnumClass, MetaMap<any>>();

function getMetaMap<T extends EnumClass>(obj: T) {
  let map = MetaMapCollection.get(obj);
  if (!map) {
    map = new Map();
    MetaMapCollection.set(obj, map);
  }
  return map as MetaMap<T>;
}

export abstract class Enum {
  static metadata<T extends EnumClass>(this: T) {
    return Object.fromEntries(getMetaMap(this).entries()) as Record<KeyOf<T>, Readonly<MetaOf<T>>>;
  }
  static metas<T extends EnumClass>(this: T) {
    // return Object.values(this.metadata());
    // Spread operator will cause compatibility issues in some cases apparently
    // return [...getMetaMap(this).values()]
    return Array.from(getMetaMap(this).values());
  }
  static meta<T extends EnumClass>(this: T, value: ValueOf<T>) {
    return this.metas().find(item => item.value === value);
  }
  static [Symbol.iterator]<T extends EnumClass>(this: T) {
    return (Object.values(this) as ValueOf<T>[]).values();
  }
}

export type EnumClass = typeof Enum;

type PartialFrom<T, U> = Omit<T, keyof U> & Partial<U>;

const defineMeta = <T extends EnumClass = EnumClass, U extends EnumClass = EnumClass>(
  meta?: PartialFrom<DefinedMetaOf<T>, DefinedMetaOf<U>>,
) => {
  return function (target: unknown, key: string | symbol | ClassFieldDecoratorContext) {
    if (key && typeof key === 'object') {
      return function (this: T, value: ValueOf<T>) {
        const map = getMetaMap(this);
        const original = map.get(key.name as KeyOf<T>);
        const data = { ...original, ...meta, key: key.name, value } as MetaOf<T>;
        map.set(key.name as KeyOf<T>, Object.freeze(data));
        return value;
      } as never;
    } else {
      // for Babel and TypeScript's `experimentalDecorators`
      const map = getMetaMap(target as T);
      // @ts-ignore
      const data = { ...meta, key, value: (target as T)[key] } as MetaOf<T>;
      map.set(key as KeyOf<T>, Object.freeze(data));
    }
  } satisfies PropertyDecorator;
};

const declareProperty = ((
  target: unknown,
  key: string | symbol | ClassFieldDecoratorContext,
  descriptor?: PropertyDescriptor,
) => {
  if (key && typeof key === 'object') {
    // for ECMAScript decorators
    // key.addInitializer(function (this: unknown) {
    //   Object.defineProperty(this, key.name, { configurable: true, enumerable: false });
    // });
    // return function (this: unknown) {
    //   Object.defineProperty(this, key.name, { configurable: true, enumerable: false });
    // };
    throw new TypeError(`Cannot declare properties with ECMAScript decorators. Use getters directly instead`);
  } else if (descriptor) {
    // for Babel
    descriptor.enumerable = false;
    return descriptor as never;
  } else {
    // for TypeScript's `experimentalDecorators`
    // @ts-ignore
    Object.defineProperty(target, key, { configurable: true, enumerable: false });
  }
  // `PropertyDecorator` is for TypeScript's `experimentalDecorators` only
}) satisfies PropertyDecorator;

function isEnumConstructor<T extends Function>(target: T): target is Extract<T, EnumClass> {
  return target.prototype instanceof Enum;
}

const inheritProperties = (<T extends Function>(target: T, context?: ClassDecoratorContext) => {
  // `context` is for ECMAScript decorators only
  if (!isEnumConstructor(target)) {
    throw new TypeError(`Class ${target.name} is not an Enum constructor`);
  }
  const parentClass: Extract<T, EnumClass> = Object.getPrototypeOf(target);
  const extensions = { ...target };
  Object.keys(target).forEach(key => {
    // @ts-ignore
    delete target[key];
  });
  Object.assign(target, parentClass, extensions);
  const targetMap = getMetaMap(target);
  getMetaMap(parentClass).forEach((value, key) => {
    const meta = targetMap.get(key);
    defineMeta<typeof target>({ ...value, ...meta })(target, key);
  });
  // `ClassDecorator` is for TypeScript's `experimentalDecorators` only
}) satisfies ClassDecorator;

export {
  /** @deprecated */
  declareProperty as declare,
  inheritProperties as inherit,
  defineMeta as meta,
};
